/**
 * Example-JSON editor for the ai_call node's expected output (an example,
 * not a schema): the backend appends it to the prompt as a structure hint
 * and its keys feed downstream autocomplete. Invalid text is never
 * committed — the last parseable value stays on the node.
 */

import { FC, KeyboardEvent, useState } from 'react'
import { useTranslate } from 'react-admin'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { JsonValue } from '../types'
import { xxllncColor } from '../../../layout/theme/color'
import FormatJsonPill from '../components/FormatJsonPill'
import { applyEditorKey, monoInputSx } from './jsonEditing'
import { valueToText } from './parameterUtils'

interface OutputExampleFieldProps {
  value?: JsonValue
  onChange: (value?: JsonValue) => void
}

const OutputExampleField: FC<OutputExampleFieldProps> = ({ value, onChange }) => {
  const translate = useTranslate()
  const [text, setText] = useState<string>(() => valueToText(value))
  const [jsonError, setJsonError] = useState(false)

  const applyText = (raw: string) => {
    setText(raw)
    if (raw.trim() === '') {
      setJsonError(false)
      onChange(undefined)
      return
    }
    try {
      onChange(JSON.parse(raw) as JsonValue)
      setJsonError(false)
    } catch {
      setJsonError(true)
    }
  }

  const handleFormat = () => {
    try {
      applyText(JSON.stringify(JSON.parse(text) as JsonValue, null, 2))
    } catch {
      // Not valid JSON — the error state already tells the user.
    }
  }

  const handleEditorKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    const el = e.target as HTMLTextAreaElement
    if (typeof el.selectionStart !== 'number') return
    const edit = applyEditorKey(text, e.key, e.shiftKey, el.selectionStart, el.selectionEnd ?? el.selectionStart)
    if (edit === null) return
    e.preventDefault()
    if (edit === 'consume') return
    applyText(edit.text)
    requestAnimationFrame(() => el.setSelectionRange(edit.caret, edit.caret))
  }

  const showFormat = !jsonError && text.trim() !== ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <TextField
        label={translate('workflow.config.outputExample')}
        value={text}
        onChange={(e) => applyText(e.target.value)}
        onKeyDown={handleEditorKeys}
        size="small"
        fullWidth
        multiline
        minRows={3}
        error={jsonError}
        helperText={translate('workflow.config.outputExampleHint')}
        placeholder={'{\n  "uuid": "…",\n  "name": "…"\n}'}
        InputProps={{ sx: monoInputSx }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 0.25, minHeight: 22 }}>
        {showFormat && <FormatJsonPill onFormat={handleFormat} />}
        {jsonError && (
          <Typography
            sx={{
              flex: 1,
              textAlign: 'right',
              fontSize: '0.65rem',
              fontWeight: 500,
              lineHeight: 1.2,
              color: xxllncColor.red100,
            }}
          >
            {translate('workflow.config.paramJsonInvalid')}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default OutputExampleField
